import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  TrendingUp, 
  Activity, 
  RefreshCw, 
  Heart, 
  Clock, 
  ShieldCheck, 
  FolderSync
} from 'lucide-react';
import { useNexoraXLedger } from '../lib/NexoraXLedgerManager';

export function NexoraXLedgerTicker() {
  const { metrics, loading, refreshLedger, setManualLiveSync } = useNexoraXLedger();
  const [useLiveDatabase, setUseLiveDatabase] = useState(true);
  const [manualSyncing, setManualSyncing] = useState(false);

  // Trigger genuine manual refetch
  const handleManualRefresh = async () => {
    setManualSyncing(true);
    try {
      await refreshLedger();
    } catch (e) {
      console.error("[NexoraX Ledger Sync] Active manual extraction failed:", e);
    } finally {
      setTimeout(() => {
        setManualSyncing(false);
      }, 600);
    }
  };

  const handleToggleLive = (active: boolean) => {
    setUseLiveDatabase(active);
    setManualLiveSync(active);
  };

  // Static snapshot fallback fallback metrics if toggled off
  const staticMetrics = {
    grossHarvest: 12500.00,
    netfinance: 7875.00,
    pipelineVector: 145000.00,
    philanthropicReserve: 125.00
  };

  const displayMetrics = useLiveDatabase 
    ? {
        grossHarvest: metrics.totalGrossHarvest,
        netfinance: metrics.netSettlement,
        pipelineVector: metrics.pipelineAmount,
        philanthropicReserve: metrics.NexoraXLevy
      }
    : staticMetrics;

  return (
    <div 
      id="NexoraX-ledger-ticker-container"
      className="NexoraX-ledger-ticker w-full bg-black border-y border-[#00ffff]/45 p-1.5 md:p-2 flex flex-col md:flex-row items-center justify-between gap-2 px-4 shadow-[0_0_15px_rgba(0,255,255,0.08)] relative z-[60]"
    >
      {/* Decorative cyber grid backdrop segment */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.01)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none opacity-30" />
      
      {/* Target/Authority badge */}
      <div className="flex items-center gap-2.5 shrink-0 select-none">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
        </span>
        <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-[0.25em] flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-cyan-400" />
          NEXORAX NEXORAX LEDGER TICKER
        </span>
      </div>

      {/* Metrics Row */}
      <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-2 font-mono text-[10.5px] w-full md:w-auto">
        
        {/* Gross Harvest */}
        <div className="flex items-center gap-1.5 bg-black/40 border border-slate-900/60 py-1 px-2.5 rounded">
          <span className="text-slate-500 uppercase tracking-widest text-[8.5px]">Gross Harvest:</span>
          <span className="text-[#00ffff] font-extrabold tracking-wider animate-pulse">
            £{displayMetrics.grossHarvest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Net finance */}
        <div className="flex items-center gap-1.5 bg-black/40 border border-slate-900/60 py-1 px-2.5 rounded">
          <span className="text-slate-500 uppercase tracking-widest text-[8.5px]">Net finance:</span>
          <span className="text-[#00ffff] font-extrabold tracking-wider">
            £{displayMetrics.netfinance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Pipeline Vector */}
        <div className="flex items-center gap-1.5 bg-black/40 border border-slate-900/60 py-1 px-2.5 rounded">
          <span className="text-slate-500 uppercase tracking-widest text-[8.5px]">Pipeline Vector:</span>
          <span className="text-[#00ffff] font-extrabold tracking-wider">
            £{displayMetrics.pipelineVector.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Philanthropic Reserve */}
        <div className="flex items-center gap-1.5 bg-black/40 border border-slate-900/60 py-1 px-2.5 rounded">
          <span className="text-slate-500 uppercase tracking-widest text-[8.5px] flex items-center gap-1">
            <Heart className="w-2.5 h-2.5 text-emerald-400 fill-emerald-500/10" />
            Philanthropic Reserve:
          </span>
          <span className="text-[#00ff22] font-extrabold tracking-wider">
            £{displayMetrics.philanthropicReserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Control interface for live mapping sync toggling */}
      <div className="flex items-center gap-2.5 shrink-0">
        <button
          onClick={() => handleToggleLive(!useLiveDatabase)}
          className={`flex items-center gap-1.5 py-1 px-2.5 rounded font-mono text-[8.5px] uppercase font-black tracking-wider transition-all cursor-pointer border ${
            useLiveDatabase 
              ? 'bg-cyan-950/35 border-cyan-500/40 text-cyan-400 shadow-[0_0_8px_rgba(0,255,255,0.1)]' 
               : 'bg-black border-slate-800 text-slate-500 hover:text-slate-300'
          }`}
          title="Toggle live database aggregates vs illustrative static snapshot"
        >
          <FolderSync className="w-2.5 h-2.5" />
          {useLiveDatabase ? 'LIVE ENGINE' : 'SNAPSHOT'}
        </button>

        <button
          onClick={handleManualRefresh}
          disabled={loading || manualSyncing}
          className="flex items-center gap-1.5 py-1 px-2.5 bg-cyan-950/20 hover:bg-[#00ffff]/10 border border-[#00ffff]/40 hover:border-[#00ffff] text-[#00ffff] rounded font-mono text-[8.5px] font-black uppercase tracking-wider transition-all cursor-pointer disabled:opacity-50"
          title="Force manual finance fetch and synchronize core allocations"
        >
          <RefreshCw className={`w-2.5 h-2.5 ${loading || manualSyncing ? 'animate-spin' : ''}`} />
          {loading || manualSyncing ? 'SYNCING...' : 'REFRESH LEDGER'}
        </button>
      </div>
    </div>
  );
}
